<template>
  <Modal
    ref="modal"
    class="license-modal-container"
  >
    <!-- eslint-disable vue/no-unused-vars -->
    <ModalCloseButton
      v-if="state === 'loaded'"
      v-slot="close"
      dark
      @click-close="handleClickClose"
    />
    <!-- eslint-enable vue/no-unused-vars -->
    <Loading
      v-if="state === 'loading'"
      message="Loading available plans..."
    />
    <Loading
      v-if="state === 'submit'"
      message="Updating your product..."
    />
    <Success
      v-if="state === 'success'"
      @close="onCancel"
    />
    <Error
      v-if="state === 'error'"
      message="There's been an issue switching plans"
      :error-message="errorMessage"
      @close="onCancel"
    />
    <div
      v-if="state === 'loaded'"
      class="change-plan-modal-container"
    >
      <div>
        <h3>Change plan</h3>
        <hr>
      </div>
      <div class="change-plan-modal-body">
        <Steps
          :current-step="currentStep"
          :steps="stepTitles"
          :is-downgrade="isDowngrade"
          @change="onStepChange"
        />
        <p class="mobile-title">
          {{ mobileTitle }}
        </p>
        <keep-alive>
          <SelectPlan
            v-if="currentStep === 0"
            :current-plan="currentPlan"
            :selected-plan="newPlan"
            :plans="dealPlans"
            @selectPlan="onSelectPlan"
          />
          <SelectPaymentMethod
            v-if="currentStep === 1"
            :payment-method="paymentMethod"
            :selected-card="selectedCard"
            :payment-total="paymentTotal"
            @selectMethod="onSelectMethod"
            @selectCard="onSelectCard"
          />
          <ReviewPlanChange
            v-if="currentStep === 2"
            :selected-card="selectedCard"
            :payment-method="paymentMethod"
            :current-plan="currentPlan"
            :new-plan="newPlan"
            :is-downgrade="isDowngrade"
            :member-discount="memberDiscount"
            :discount-type="discountType"
          />
        </keep-alive>
      </div>
      <div class="change-plan-modal-footer">
        <Button
          class="cancel-button"
          color="primary"
          plain
          @click="onCancel"
        >
          Cancel
        </Button>
        <Button
          v-show="currentStep !== 2 || currentStep === 2 && paymentMethod !== 'paypal'"
          class="continue-button"
          :disabled="!(currentStep <= dataForStep)"
          :color="currentStep === 2 ? 'warning' : 'primary'"
          @click="onContinue"
        >
          {{ nextStep }}
        </Button>
        <div
          v-show="currentStep === 2 && paymentMethod === 'paypal'"
          ref="paypalContainer"
          class="paypal-container"
        />
      </div>
    </div>
  </Modal>
</template>
<script>

import axios from 'axios';
import Vue from 'vue';
import Modal from '../utilities/Modal.vue';
import ModalCloseButton from '../utilities/ModalCloseButton.vue';
import Button from '../ui/Button.vue';
import Steps from '../ui/Steps.vue';
import Loading from '../ui/Loading.vue';
import Error from '../ui/Error.vue';
import Success from './Success.vue';

import SelectPlan from './SelectPlan.vue';
import SelectPaymentMethod from './SelectPaymentMethod.vue';
import ReviewPlanChange from './ReviewPlanChange.vue';

export default {
  components: {
    Modal,
    ModalCloseButton,
    Button,
    Steps,
    SelectPlan,
    SelectPaymentMethod,
    ReviewPlanChange,
    Loading,
    Success,
    Error,
  },
  props: {
    deal: {
      type: String,
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
    isRefundDateExpired: {
      type: Boolean,
      required: false,
    },
    currentPrice: {
      type: String,
      required: false,
    },
    hasPlus: {
      type: Boolean,
      required: false,
    },
    hasBriefcase: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      state: 'loading',
      errorMessage: '',
      dealPlans: [],

      currentPlan: {},
      newPlan: {},
      isDowngrade: false,
      paymentMethod: null,
      selectedCard: null,
      paymentTotal: null,

      currentStep: 0,
      dataForStep: -1,
      nextStep: 'Continue to Payment',
      stepTitles: ['Select Plan', 'Select Payment Method', 'Review Selection'],
    };
  },
  computed: {
    discountType() {
      return this.hasPlus ? 'Plus' : 'Briefcase';
    },
    memberDiscount() {
      if (this.hasPlus || this.hasBriefcase) {
        return (Number(this.newPlan.price) - Number(this.currentPlan.price)) * 0.10;
      }

      return 0;
    },
    mobileTitle() {
      return this.stepTitles[this.currentStep];
    },
  },
  watch: {
    newPlan() {
      this.calculateSteps();
    },
    paymentMethod() {
      this.calculateSteps();
    },
    selectedCard() {
      this.calculateSteps();
    },
  },
  created() {
    Promise.all([
      this.fetchPlans(),
      this.fetchCurrentPlan(),
    ]).then(() => {
      this.state = 'loaded';
    });
  },
  mounted() {
    this.$nextTick(() => {
      if (typeof paypal !== 'undefined' && typeof paypal.Buttons !== 'undefined') { // eslint-disable-line no-undef
        this.initPaypalButton();
      } else {
        Vue.loadScript(`https://www.paypal.com/sdk/js?client-id=${process.env.VUE_APP_PAYPAL_CLIENT_ID}&debug=false&intent=capture`)
          .then(() => {
            this.initPaypalButton();
          });
      }
    });

    Vue.loadScript('https://js.stripe.com/v3/');
  },
  methods: {
    initPaypalButton() {
      if (!this.$refs.paypalContainer) {
        // race condition keep trying until Vue element is available
        return setTimeout(() => this.initPaypalButton(), 100);
      }
      return paypal.Buttons({ // eslint-disable-line no-undef
        style: {
          size: 'responsive',
          layout: 'horizontal',
          color: 'gold',
          shape: 'rect',
          height: 49,
          label: 'pay',
          tagline: false,
        },
        createOrder: (data, actions) => {
          const amount = this.newPlan.price - this.currentPlan.price - this.memberDiscount;
          return actions.order.create({
            intent: 'CAPTURE',
            application_context: {
              shipping_preference: 'NO_SHIPPING',
              user_action: 'PAY_NOW',
            },
            purchase_units: [{
              description: `
                ${this.newPlan.deal.public_name} - Upgrade to Tier ${this.newPlan.tier}
              `,
              soft_descriptor: 'AppSumo',
              amount: {
                value: amount,
              },
            }],
          });
        },
        onApprove: (data) => {
          const paypalOrderId = data.orderID;
          this.switchPlan({ paypalOrderId });
        },
        onCancel: () => {
          this.handleClickClose();
        },
      }).render(this.$refs.paypalContainer);
    },
    handleClickClose() {
      this.$refs.modal.handleClickClose();
    },
    calculateSteps() {
      if (this.newPlan !== null && this.newPlan.id !== this.currentPlan.id) this.dataForStep = 0;
      if (this.dataForStep > -1) {
        if (this.isDowngrade) {
          this.dataForStep = 2;
        } else if (this.paymentMethod !== null) {
          if (
            this.paymentMethod !== 'creditcard'
              || (this.paymentMethod === 'creditcard' && this.selectedCard)
          ) {
            this.dataForStep = 2;
          }
        }
      }
    },
    async fetchCurrentPlan() {
      const itemsUrl = `${process.env.VUE_APP_DJ_BASE_URL
      }api/userinvoices/?format=json&include[]=deal_plan&include[]=deal&exclude[]=invoice.*`
        + '&include[]=invoice.payment_type&include[]=invoice.enhanced_invoice_items'
        + `&include[]=invoice.total&filter{uuid}=${this.uuid}`;

      return axios
        .get(itemsUrl, { withCredentials: true })
        .then((response) => {
          const { data } = response;
          const [item] = data.invoiceitems;
          const {
            deal,
            invoice,
            original_invoice_payment_type, // eslint-disable-line camelcase
            refund_info, // eslint-disable-line camelcase
            lowest_available_plan_tier, // eslint-disable-line camelcase
            can_enhance_license, // eslint-disable-line camelcase
            deal_plan: plan,
          } = item;

          this.currentPlan = {
            ...plan,
            deal,
            invoice,
            refund_info,
            original_invoice_payment_type,
            lowest_available_plan_tier,
            can_enhance_license,
            uuid: item.uuid,
            license_key: item.license_key,
          };
        });
    },
    async fetchPlans() {
      let plansUrl = `${process.env.VUE_APP_DJ_BASE_URL
      }api/dealplans/?format=json`
        + '&filter{is_active}=true&include[]=plan_features'
        + `&include[]=deal&filter{deal.id}=${this.deal}`;

      if (this.isRefundDateExpired) {
        // exclude lower plans because refund is not longer allowed
        plansUrl += `&price=${this.currentPrice}`;
      }

      return axios
        .get(plansUrl, { withCredentials: true })
        .then((response) => {
          const { data } = response;
          const { plans } = data;

          plans.forEach((plan) => {
            const { plan_features: features } = plan;
            features.forEach((feature) => {
              if (feature.code_to_feature_conf) {
                const entry = feature.code_to_feature_conf.find((c) => c.codes === plan.tier);

                if (entry) {
                  // eslint-disable-next-line no-param-reassign
                  feature.feature = feature.feature.replace('{{}}', entry.quantity);
                }
              }
            });
            // eslint-disable-next-line no-param-reassign
            plan.plan_features = plan.plan_features.slice(0, 6);
          });

          this.dealPlans = plans;
        });
    },
    selectStep(step) {
      if (step - 1 > this.dataForStep) return;

      // skip payment method screen if downgrading
      if (step === 1 && this.isDowngrade) step = 2; // eslint-disable-line no-param-reassign

      this.currentStep = step;
      switch (this.currentStep) {
        case 0: this.nextStep = 'Continue to Payment'; break;
        case 1: this.nextStep = 'Continue to Review'; break;
        case 2: this.nextStep = 'Change Plan'; break;
        default: break;
      }
    },
    onSelectPlan(planId) {
      this.newPlan = this.dealPlans.find((p) => p.id === planId);

      if (this.newPlan && this.currentPlan) {
        this.isDowngrade = parseFloat(this.newPlan.price) <= parseFloat(this.currentPlan.price);
      }
      this.setPaymentTotal();
      this.calculateSteps();
    },
    onSelectMethod(method) {
      this.paymentMethod = method;
    },
    onSelectCard(card) {
      this.selectedCard = card;
    },
    onStepChange(step) {
      this.selectStep(step);
    },
    onCancel() {
      window.vueAPI.closeModal();

      if (this.currentStep === 2 && this.state === 'success') {
        location.href = '/account/'; // eslint-disable-line no-restricted-globals
      }
    },
    onContinue() {
      const curr = this.currentStep;

      if (curr === 0) {
        this.selectStep(this.isDowngrade ? 2 : 1);
      } else if (curr === 1) {
        this.selectStep(this.currentStep + 1);
      } else if (curr === 2) {
        this.switchPlan();
      }
    },
    switchPlan({ paypalOrderId } = {}) {
      const switchUrl = `${process.env.VUE_APP_DJ_BASE_URL
      }license/update/${
        this.currentPlan.license_key
      }/`;

      const data = {
        action: this.isDowngrade ? 'reduce_tier' : 'enhance_tier',
        plan: this.newPlan.id,
      };

      if (this.isDowngrade) {
        data.refund_type = 'default';
      } else {
        data.payment_method = this.paymentMethod;
        if (data.payment_method === 'creditcard' && this.selectedCard) {
          data.payment_method = 'stripe';
          data.stripe_token = this.selectedCard.id;
        } else if (this.paymentMethod === 'paypal') {
          data.paypal_order_id = paypalOrderId;
        }
      }

      this.state = 'submit';
      const csrfToken = this.$cookies.get('csrftoken');
      return axios({
        method: 'put',
        url: switchUrl,
        withCredentials: true,
        headers: {
          'X-CSRFToken': csrfToken,
        },
        data,
      })
        .then(() => {
          this.state = 'success';
        })
        .catch((err) => {
          this.state = 'error';
          this.errorMessage = err.response.data.message;
        });
    },
    setPaymentTotal() {
      if (this.newPlan.price && this.currentPlan.price && !this.isDowngrade) {
        const total = (
          Number(this.newPlan.price)
          - Number(this.currentPlan.price)
          - Number(this.memberDiscount)
        );
        this.paymentTotal = total.toString();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../variables.scss";

.change-plan-modal-container {
  padding: 30px;
  font-family: "Metropolis", sans-serif;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.change-plan-modal-body {
  flex-grow: 1;
  margin-top: 25px;
}

.change-plan-modal-footer {
  text-align: right;
  border-top: 1px solid #E5E5E9;
  padding-top: 20px;
  margin-top: 30px;
}

.btn.cancel-button.primary.plain {
  font-weight: 300;
  margin-right: 20px;
}

.mobile-title {
  display: none;
}

.paypal-container {
  display: inline-block;
  position: relative;
  top: 21px;
  height: 49px;

  @media screen and (max-width: $license-modal-mobile) {
    width: 100%;
  }
}

@media screen and (min-width: 800px) {
  .change-plan-modal-container {
    max-width: 1000px;
    width: 100%;
  }
}

@media screen and (max-width: $license-modal-mobile) {
  .change-plan-modal-container {
    padding: 15px;
  }
  .mobile-title {
    display: block;
    font-size: 20px;
    font-weight: 600;
    margin-top: 15px;
  }
  .cancel-button {
    display: none;
  }
  .continue-button{
    width: 100%;
  }
}

</style>
